// src/features/announcements/announcementSlice.js
import { createSlice } from '@reduxjs/toolkit';
import {
  fetchAnnouncementsThunk,
  fetchAnnouncementThunk,
  createAnnouncementThunk,
  updateAnnouncementThunk,
  deleteAnnouncementThunk,
  fetchAnnouncementsByStoreThunk,
  fetchAnnouncementsTableViewThunk,
  fetchTodayAnnouncementsThunk,
  fetchAnnouncementsByEmployeeThunk,
  fetchAnnouncementStatsThunk,
  checkCanEditThunk,
} from './announcementThunks';

const initialState = {
  announcements: [],
  currentAnnouncement: null,
  loading: false,
  error: null,
  
  // Table view data (grouped by store and date)
  tableView: {
    data: [],
    loading: false,
    error: null,
  },
  
  // Grouped by store
  storeGroups: {
    data: [],
    loading: false,
    error: null,
  },
  
  // Today's announcements
  todayAnnouncements: {
    data: [],
    loading: false,
    error: null,
  },
  
  // Grouped by employee
  employeeGroups: {
    data: [],
    loading: false,
    error: null,
  },
  
  // Statistics
  stats: {
    data: null,
    loading: false,
    error: null,
  },
  
  // Filters
  filters: {
    store: null,
    announced_by: null,
    date: null,
    start_date: null,
    end_date: null,
  },
};

const announcementSlice = createSlice({
  name: 'announcements',
  initialState,
  reducers: {
    clearCurrentAnnouncement(state) {
      state.currentAnnouncement = null;
    },
    
    setFilters(state, action) {
      state.filters = { ...state.filters, ...action.payload };
    },
    
    clearFilters(state) {
      state.filters = { ...initialState.filters };
    },
    
    resetError(state) {
      state.error = null;
    },
    
    clearTableView(state) {
      state.tableView = { ...initialState.tableView };
    },
    
    clearStoreGroups(state) {
      state.storeGroups = { ...initialState.storeGroups };
    },
    
    clearTodayAnnouncements(state) {
      state.todayAnnouncements = { ...initialState.todayAnnouncements };
    },
    
    clearEmployeeGroups(state) {
      state.employeeGroups = { ...initialState.employeeGroups };
    },
    
    clearStats(state) {
      state.stats = { ...initialState.stats };
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch all announcements
      .addCase(fetchAnnouncementsThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAnnouncementsThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.announcements = action.payload;
      })
      .addCase(fetchAnnouncementsThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Fetch single announcement
      .addCase(fetchAnnouncementThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAnnouncementThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.currentAnnouncement = action.payload;
      })
      .addCase(fetchAnnouncementThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Create announcement
      .addCase(createAnnouncementThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createAnnouncementThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.announcements.unshift(action.payload);
        state.currentAnnouncement = action.payload;
      })
      .addCase(createAnnouncementThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Update announcement
      .addCase(updateAnnouncementThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateAnnouncementThunk.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.announcements.findIndex(a => a.id === action.payload.id);
        if (index !== -1) {
          state.announcements[index] = action.payload;
        }
        if (state.currentAnnouncement && state.currentAnnouncement.id === action.payload.id) {
          state.currentAnnouncement = action.payload;
        }
      })
      .addCase(updateAnnouncementThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Delete announcement
      .addCase(deleteAnnouncementThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteAnnouncementThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.announcements = state.announcements.filter(a => a.id !== action.payload);
        if (state.currentAnnouncement && state.currentAnnouncement.id === action.payload) {
          state.currentAnnouncement = null;
        }
      })
      .addCase(deleteAnnouncementThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Fetch announcements by store
      .addCase(fetchAnnouncementsByStoreThunk.pending, (state) => {
        state.storeGroups.loading = true;
        state.storeGroups.error = null;
      })
      .addCase(fetchAnnouncementsByStoreThunk.fulfilled, (state, action) => {
        state.storeGroups.loading = false;
        state.storeGroups.data = action.payload;
      })
      .addCase(fetchAnnouncementsByStoreThunk.rejected, (state, action) => {
        state.storeGroups.loading = false;
        state.storeGroups.error = action.payload;
      })
      
      // Fetch table view
      .addCase(fetchAnnouncementsTableViewThunk.pending, (state) => {
        state.tableView.loading = true;
        state.tableView.error = null;
      })
      .addCase(fetchAnnouncementsTableViewThunk.fulfilled, (state, action) => {
        state.tableView.loading = false;
        state.tableView.data = action.payload;
      })
      .addCase(fetchAnnouncementsTableViewThunk.rejected, (state, action) => {
        state.tableView.loading = false;
        state.tableView.error = action.payload;
      })
      
      // Fetch today's announcements
      .addCase(fetchTodayAnnouncementsThunk.pending, (state) => {
        state.todayAnnouncements.loading = true;
        state.todayAnnouncements.error = null;
      })
      .addCase(fetchTodayAnnouncementsThunk.fulfilled, (state, action) => {
        state.todayAnnouncements.loading = false;
        state.todayAnnouncements.data = action.payload;
      })
      .addCase(fetchTodayAnnouncementsThunk.rejected, (state, action) => {
        state.todayAnnouncements.loading = false;
        state.todayAnnouncements.error = action.payload;
      })
      
      // Fetch announcements by employee
      .addCase(fetchAnnouncementsByEmployeeThunk.pending, (state) => {
        state.employeeGroups.loading = true;
        state.employeeGroups.error = null;
      })
      .addCase(fetchAnnouncementsByEmployeeThunk.fulfilled, (state, action) => {
        state.employeeGroups.loading = false;
        state.employeeGroups.data = action.payload;
      })
      .addCase(fetchAnnouncementsByEmployeeThunk.rejected, (state, action) => {
        state.employeeGroups.loading = false;
        state.employeeGroups.error = action.payload;
      })
      
      // Fetch statistics
      .addCase(fetchAnnouncementStatsThunk.pending, (state) => {
        state.stats.loading = true;
        state.stats.error = null;
      })
      .addCase(fetchAnnouncementStatsThunk.fulfilled, (state, action) => {
        state.stats.loading = false;
        state.stats.data = action.payload;
      })
      .addCase(fetchAnnouncementStatsThunk.rejected, (state, action) => {
        state.stats.loading = false;
        state.stats.error = action.payload;
      })
      
      // Check can edit
      .addCase(checkCanEditThunk.fulfilled, (state, action) => {
        // Update can_edit flag in current announcement if it matches
        if (state.currentAnnouncement && 
            state.currentAnnouncement.id === action.payload.announcementId) {
          state.currentAnnouncement.can_edit = action.payload.can_edit;
        }
      });
  },
});

export const {
  clearCurrentAnnouncement,
  setFilters,
  clearFilters,
  resetError,
  clearTableView,
  clearStoreGroups,
  clearTodayAnnouncements,
  clearEmployeeGroups,
  clearStats,
} = announcementSlice.actions;

export default announcementSlice.reducer;